import type {
  BilingualLabel,
  CatalogBillingPromptLengthTiers,
  CatalogBillingPromptTierBasis,
  CatalogBillingSurcharge,
  CatalogBillingTierRow,
  CatalogBillingTiers,
  CatalogBillingTokenRates,
  CatalogSku,
  CatalogSkuBilling,
  CatalogSkuSaleSchedule,
} from '@nadoutong/contracts';
import type { CnyRateDecimals } from '../../components/formatters.ts';
import {
  cnyRateDecimalsForCard,
  formatCnyRateFromUsdRate,
  formatUsdMicrosAsCnyRate,
} from '../../components/formatters.ts';
import type { I18nKey, Locale } from '../../i18n/index.ts';
import { useLocale, useT } from '../../i18n/index.ts';
import { orderAxisValues } from './axisOrder.ts';
import {
  billingTokenCardLines,
  hasRateDiscount,
  PriceLine,
  pricingLinesForModel,
  UnitPriceLine,
  UnitPricingCell,
} from './priceLines.tsx';
import { formatMinuteOfDay, weekdayIndexesFromMask, weekdaySpans } from './saleWindows.ts';
import { trimCnyTrailingZeros } from './trimCnyTrailingZeros.ts';

/**
 * tenant#117 — renderer for the self-describing catalog billing document
 * (`CatalogSku.billing`, router#1883), reworked against the backend's
 * per-SKU display checklist (dev, 2026-08). ONE renderer covers every
 * geometry the document can express, so a new sales form (a new tier
 * scheme, a new unit) arrives as new DATA with zero frontend change:
 *
 *   ① flat token (`mode: 'token'`, `tiers: null`) — the document's own
 *     `token_rates` card: up to seven buckets, `null` hides a row, `0`
 *     renders "Not billed" (checklist gap #4). Only a catalog that does
 *     not carry the `billing` key at all still falls through to the
 *     incumbent flat-field rendering, byte-identical to today;
 *   ② tiered token (`tiers` non-null, single-rate or seven-bucket rows) —
 *     a tier row list with backend-issued bilingual labels;
 *   ③ unit (`mode: 'unit'`) — tiered rows or the untiered per-unit line;
 *     no unit and no rates is the "pricing pending" placeholder.
 *
 * Two-axis tiers with one price per cell — ② single-rate and ③-a — render
 * as a REAL matrix (rows = first axis, columns = second axis, unpriced
 * cells em-dashed; checklist gap #2); every other tier shape keeps the
 * row list. Axis values display in semantic order (`axisOrder.ts`,
 * gap #3). Every price on this surface is BASE × `rate_multiplier` ×
 * fx — the checklist's rule #1; the document never carries a multiplied
 * price.
 *
 * Conservative-fallback rules (the issue's hard requirement — better no
 * price than a wrong price). tenant#138 restates the same iron rule and
 * adds: unrecognized `rates` shapes render NOTHING for that row; money
 * is never computed client-side beyond formatting backend values (CNY
 * via the display-rate plumbing — never a hardcoded 7).
 *   - unknown `presentation_version` → a "see pricing notes" placeholder,
 *     no price details at all (on a tiered SKU the flat fields are the
 *     anchor row, so falling back to them would BE the wrong price).
 *     Additive members (`surcharges`, `prompt_length_tiers`,
 *     `image_input_per_mtok_usd`, SKU `sale_schedule`) do NOT bump the
 *     version; this reader renders them on v1. A bumped version still
 *     falls back rather than guessing a new shape;
 *   - a rates member this reader does not recognize → nothing for that
 *     row (no guessed figure);
 *   - the anchor `token_rates` of a variant-tiered SKU is never displayed
 *     (it may be a deliberate zero anchor). Prompt-length tiers are the
 *     other axis: `token_rates` IS the base row of an N+1 list.
 *
 * All tier/axis/unit labels are consumed verbatim from the document's
 * `label.zh/en` — the frontend owns only frame words (placeholders, the
 * "Per {unit}" template, prompt-tier / surcharge / sale-window chrome),
 * never a tier vocabulary.
 */

/**
 * The ONE presentation syntax version this renderer understands.
 * Deliberately a local constant rather than the contract's
 * `CATALOG_BILLING_PRESENTATION_VERSION`: a vendored-contracts sync that
 * bumps the constant must NOT silently make this reader claim support for
 * a shape it has no code for — an unknown version must keep falling back.
 */
const SUPPORTED_PRESENTATION_VERSION = 1;

export type CatalogBillingView =
  | { readonly kind: 'legacy' }
  | { readonly kind: 'fallback' }
  | {
      readonly kind: 'token_flat';
      readonly rates: CatalogBillingTokenRates;
      readonly promptLengthTiers: CatalogBillingPromptLengthTiers | null;
    }
  | {
      readonly kind: 'tiered';
      readonly tiers: CatalogBillingTiers;
      readonly unitLabel: BilingualLabel | null;
    }
  | {
      readonly kind: 'unit_flat';
      readonly unitLabel: BilingualLabel;
      readonly rateUsdMicros: number;
    }
  | { readonly kind: 'unit_pending' };

/** Runtime shape guard for wire data the static types cannot vouch for. */
function isRecordObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Decide which renderer a SKU's billing document gets. Only `undefined`
 * (the pre-deployment catalog without the key) resolves to `'legacy'`;
 * a present document is always the display source, geometry ① included.
 */
export function classifyCatalogBilling(billing: CatalogSkuBilling | undefined): CatalogBillingView {
  if (billing == null || !isRecordObject(billing)) {
    return { kind: 'legacy' };
  }
  if (billing.presentation_version !== SUPPORTED_PRESENTATION_VERSION) {
    return { kind: 'fallback' };
  }
  const mode = billing.mode;
  if (mode !== 'token' && mode !== 'unit') {
    // The mode set is closed in the contract, but the wire may be newer
    // than this reader; an unknown mode is an unknown document.
    return { kind: 'fallback' };
  }
  const tiers = billing.tiers;
  const promptLengthTiers = readPromptLengthTiers(billing.prompt_length_tiers);
  // tenant#138: `tiers` (variant matrix) and `prompt_length_tiers` are
  // mutually exclusive on the wire. The charge kernel drops the ladder
  // when a variant row hits, so publishing both would be a price list
  // no request can satisfy. Absent-or-one: both present → conservative
  // fallback rather than a combined (wrong) list.
  if (tiers != null && promptLengthTiers !== null) {
    return { kind: 'fallback' };
  }
  if (tiers != null) {
    if (
      !isRecordObject(tiers) ||
      !Array.isArray(tiers.axes) ||
      !Array.isArray(tiers.rows) ||
      !tiers.axes.every(
        (axis) =>
          isRecordObject(axis) && typeof axis.key === 'string' && Array.isArray(axis.values),
      ) ||
      !tiers.rows.every(
        (row) => isRecordObject(row) && isRecordObject(row.variant) && isRecordObject(row.rates),
      )
    ) {
      return { kind: 'fallback' };
    }
    // Body geometry only. `surcharges` are legal on this branch (unit-lane
    // grok video puts the reference-image row there) and are appended by
    // SkuPricesCell — they used to be dropped here (tenant#138).
    return {
      kind: 'tiered',
      tiers,
      unitLabel: billing.unit == null ? null : billing.unit.label,
    };
  }
  if (mode === 'token') {
    // Geometry ①: the flat token card, read from the DOCUMENT's own
    // `token_rates` (checklist gap #4) — the SKU row's flat fields cannot
    // express null-vs-0 or the 5m/1h/reasoning buckets per-document.
    // (tenant#117 originally fell through to the flat fields here; the
    // checklist made the document the display source.)
    // tenant#138: when `prompt_length_tiers` is present, `token_rates`
    // is the base row of an N+1 list, not a zero-anchor to hide.
    const tokenRates = billing.token_rates;
    if (!isRecordObject(tokenRates)) {
      // A token document with no rate card at all: nothing truthful to
      // render — same trade as an unknown version.
      return { kind: 'fallback' };
    }
    return { kind: 'token_flat', rates: tokenRates, promptLengthTiers };
  }
  const unitRate = billing.unit_rate;
  if (
    unitRate != null &&
    isRecordObject(unitRate) &&
    typeof unitRate.rate_usd_micros_per_unit === 'number' &&
    Number.isFinite(unitRate.rate_usd_micros_per_unit)
  ) {
    const unit = billing.unit;
    if (unit == null || !isRecordObject(unit)) {
      // A per-unit price whose unit the document does not name cannot be
      // rendered truthfully; same trade as an unknown version.
      return { kind: 'fallback' };
    }
    return {
      kind: 'unit_flat',
      unitLabel: unit.label,
      rateUsdMicros: unitRate.rate_usd_micros_per_unit,
    };
  }
  // `unit: null` and no rates at all — pricing not configured yet, exactly
  // as `unit_pricing.rates: []` already means (#1706).
  return { kind: 'unit_pending' };
}

/**
 * Whether the group badge over this SKU must go neutral ("Prices · CNY"):
 * true whenever the row can render anything that is not a per-Mtok price —
 * the #1706 unit projection, or any billing document beyond the flat token
 * card (tiers, unit prices, the placeholders). Neutral is never wrong;
 * "per 1M tokens" over a per-second price is (#1706's rule, extended).
 * `token_flat` stays on the per-Mtok badge: every line the document card
 * renders IS a per-Mtok price, exactly like the legacy flat fields.
 */
export function needsNeutralPricingBadge(sku: CatalogSku): boolean {
  if (sku.unit_pricing !== undefined) {
    return true;
  }
  // tenant#138: a surcharge is a per-unit line on top of the body, so a
  // token card that also lists "per call" / "per image" cannot honestly
  // wear the per-Mtok badge.
  if (readSurcharges(sku.billing).length > 0) {
    return true;
  }
  const kind = classifyCatalogBilling(sku.billing).kind;
  return kind !== 'legacy' && kind !== 'token_flat';
}

/** One tier row's rates member, resolved by field presence (router#1883). */
type TierRatesView =
  | { readonly kind: 'token_card'; readonly rates: CatalogBillingTokenRates }
  | { readonly kind: 'single_rate'; readonly perMtokUsd: number | null }
  | { readonly kind: 'unit_rate'; readonly rateUsdMicros: number }
  | { readonly kind: 'unknown' };

const UNKNOWN_TIER_RATES: TierRatesView = { kind: 'unknown' };

/**
 * `rows[].rates` is a union dispatched ON FIELD PRESENCE, per the contract
 * and tenant#138 form ⑥: `rate_usd_micros_per_unit` → unit price,
 * `per_mtok_usd` → single-rate token price (ONE number, never restated
 * as seven buckets), `input_per_mtok_usd` → the token card. A member
 * carrying none of these — or more than one, a combination no shipped
 * member has — is unknown, and an unknown member renders NOTHING.
 */
function classifyTierRates(rates: CatalogBillingTierRow['rates']): TierRatesView {
  if ('rate_usd_micros_per_unit' in rates) {
    if ('per_mtok_usd' in rates || 'input_per_mtok_usd' in rates) {
      return UNKNOWN_TIER_RATES;
    }
    const value = rates.rate_usd_micros_per_unit;
    return typeof value === 'number' && Number.isFinite(value)
      ? { kind: 'unit_rate', rateUsdMicros: value }
      : UNKNOWN_TIER_RATES;
  }
  if ('per_mtok_usd' in rates) {
    if ('input_per_mtok_usd' in rates) {
      return UNKNOWN_TIER_RATES;
    }
    const value = rates.per_mtok_usd;
    return value === null || (typeof value === 'number' && Number.isFinite(value))
      ? { kind: 'single_rate', perMtokUsd: value }
      : UNKNOWN_TIER_RATES;
  }
  if ('input_per_mtok_usd' in rates) {
    return { kind: 'token_card', rates };
  }
  return UNKNOWN_TIER_RATES;
}

/** Pick the current locale's side of a backend-issued bilingual label. */
function pickLabel(label: BilingualLabel, locale: Locale): string {
  if (!isRecordObject(label)) {
    return '';
  }
  const value = locale === 'zh-CN' ? label.zh : label.en;
  return typeof value === 'string' ? value : '';
}

/**
 * The tier row's display label: the row's value on every axis, in axis
 * order, through the backend-issued labels. A value key no axis lists
 * still renders — verbatim, the same way #1706 renders variant keys — so
 * a priced row is never dropped over a label lookup.
 */
function tierRowLabel(
  axes: CatalogBillingTiers['axes'],
  variant: CatalogBillingTierRow['variant'],
  locale: Locale,
): string {
  const parts: string[] = [];
  for (const axis of axes) {
    const valueKey = variant[axis.key];
    if (typeof valueKey !== 'string') {
      continue;
    }
    const value = axis.values.find(
      (candidate) => isRecordObject(candidate) && candidate.key === valueKey,
    );
    const label = value === undefined ? '' : pickLabel(value.label, locale);
    parts.push(label === '' ? valueKey : label);
  }
  return parts.length === 0 ? '—' : parts.join(' · ');
}

/** Stable React key for a tier row: its cell coordinates. */
function tierRowKey(row: CatalogBillingTierRow): string {
  return Object.entries(row.variant)
    .map(([field, value]) => `${field}:${value}`)
    .sort()
    .join('|');
}

/**
 * The prices cell of one SKU market row. Dispatches the billing document
 * to the geometry renderers; `'legacy'` (key absent, or geometry ①) is
 * the untouched incumbent branch.
 */
export function SkuPricesCell({
  sku,
  cnyPerUsd,
}: {
  readonly sku: CatalogSku;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const view = classifyCatalogBilling(sku.billing);
  switch (view.kind) {
    case 'fallback':
      return (
        <p className="m-0 text-body text-ink-low" data-testid="pricing-billing-fallback">
          {t('pricing.billing.fallback')}
        </p>
      );
    case 'token_flat': {
      const body =
        view.promptLengthTiers === null ? (
          <RatesMember
            rates={view.rates}
            rateMultiplier={sku.rate_multiplier}
            baseRateMultiplier={sku.base_rate_multiplier}
            cnyPerUsd={cnyPerUsd}
            tokenCardTestId="pricing-billing-token-flat"
          />
        ) : (
          <PromptLengthPriceList
            baseRates={view.rates}
            promptLengthTiers={view.promptLengthTiers}
            rateMultiplier={sku.rate_multiplier}
            baseRateMultiplier={sku.base_rate_multiplier}
            cnyPerUsd={cnyPerUsd}
          />
        );
      return withBillingExtras(body, sku, locale, cnyPerUsd, true);
    }
    case 'tiered':
      return withBillingExtras(
        <TieredPrices
          sku={sku}
          tiers={view.tiers}
          unitLabel={view.unitLabel}
          cnyPerUsd={cnyPerUsd}
          locale={locale}
        />,
        sku,
        locale,
        cnyPerUsd,
        true,
      );
    case 'unit_flat':
      if (sku.unit_pricing !== undefined) {
        // The #1706 projection carries the same information with
        // per-variant detail; prefer it so the incumbent unit rendering
        // never regresses. Audited against the checklist's rule #1
        // (2026-08): the projection's `rate_usd_micros_per_unit` is BASE
        // micro-USD exactly like `billing.unit_rate` — router's
        // `unit-pricing-projection.ts` states "no multiplier is applied
        // here" — and `UnitPricingCell` sends it through the same
        // `micros × rate_multiplier × fx` chain as the branch below, so
        // the two paths price identically and the preference is safe.
        // Do NOT also append billing.surcharges: the projection already
        // lists every (unit, variant) row.
        return withBillingExtras(
          <LegacyPrices sku={sku} cnyPerUsd={cnyPerUsd} />,
          sku,
          locale,
          cnyPerUsd,
          false,
        );
      }
      return withBillingExtras(
        <dl
          className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3"
          data-testid="pricing-billing-unit-flat"
        >
          <UnitPriceLine
            label={t('pricing.billing.per_unit', { unit: pickLabel(view.unitLabel, locale) })}
            rateUsdMicros={view.rateUsdMicros}
            rateMultiplier={sku.rate_multiplier}
            baseRateMultiplier={sku.base_rate_multiplier}
            cnyPerUsd={cnyPerUsd}
          />
        </dl>,
        sku,
        locale,
        cnyPerUsd,
        true,
      );
    case 'unit_pending':
      if (sku.unit_pricing !== undefined) {
        return withBillingExtras(
          <LegacyPrices sku={sku} cnyPerUsd={cnyPerUsd} />,
          sku,
          locale,
          cnyPerUsd,
          false,
        );
      }
      return withBillingExtras(
        <p className="m-0 text-body text-ink-low" data-testid="pricing-unit-pending">
          {t('pricing.rate.unit.pending')}
        </p>,
        sku,
        locale,
        cnyPerUsd,
        true,
      );
    case 'legacy':
      return withBillingExtras(
        <LegacyPrices sku={sku} cnyPerUsd={cnyPerUsd} />,
        sku,
        locale,
        cnyPerUsd,
        false,
      );
  }
}

/**
 * Geometry ① and the no-`billing` catalog: the incumbent rendering,
 * verbatim — #1706 unit rows when the SKU carries `unit_pricing`,
 * otherwise the flat per-Mtok card from the SKU row's own price fields.
 * The document's `token_rates` is deliberately never read here: on a
 * tiered SKU it is the anchor row (possibly a deliberate zero anchor)
 * and must never be shown as a price.
 */
function LegacyPrices({
  sku,
  cnyPerUsd,
}: {
  readonly sku: CatalogSku;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  if (sku.unit_pricing !== undefined) {
    return (
      <UnitPricingCell
        unitPricing={sku.unit_pricing}
        rateMultiplier={sku.rate_multiplier}
        baseRateMultiplier={sku.base_rate_multiplier}
        cnyPerUsd={cnyPerUsd}
      />
    );
  }
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3 xl:grid-cols-5">
      {pricingLinesForModel(sku).map((line) => (
        <PriceLine
          key={line.labelKey}
          label={t(line.labelKey)}
          value={line.value}
          notBilled={line.notBilled}
          notBilledLabel={t('pricing.rate.not_billed')}
          rateMultiplier={sku.rate_multiplier}
          baseRateMultiplier={sku.base_rate_multiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </dl>
  );
}

/**
 * Geometry ① when the SKU carries a billing document: the token card read
 * from `token_rates` under the checklist's null/0 rule (gap #4) — up to
 * seven buckets, a `null` dimension does not exist and renders no row, a
 * `0` dimension renders "Not billed". A card whose dimensions are ALL
 * null has nothing truthful to show and renders the "pricing pending"
 * placeholder — never an empty cell and never ¥0.00.
 */
function BillingTokenCard({
  rates,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  testId,
}: {
  readonly rates: CatalogBillingTokenRates;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly testId?: string;
}): JSX.Element {
  const t = useT();
  const lines = billingTokenCardLines(rates);
  if (lines.length === 0) {
    return (
      <p className="m-0 text-body text-ink-low" data-testid="pricing-unit-pending">
        {t('pricing.rate.unit.pending')}
      </p>
    );
  }
  const decimals = tokenCardDecimals(lines, rateMultiplier, baseRateMultiplier, cnyPerUsd);
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3" data-testid={testId}>
      {lines.map((line) => (
        <PriceLine
          key={line.labelKey}
          label={t(line.labelKey)}
          value={line.value}
          notBilled={line.notBilled}
          notBilledLabel={t('pricing.rate.not_billed')}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
          decimals={decimals}
        />
      ))}
    </dl>
  );
}

/**
 * Card precision for a set of token-card lines: every CNY amount the card
 * will show — effective and, under a discount, the struck base — feeds the
 * jiao scan. "Not billed" lines render a word, not a number, and stay out.
 */
function tokenCardDecimals(
  lines: ReadonlyArray<{ readonly value: number; readonly notBilled: boolean }>,
  rateMultiplier: number,
  baseRateMultiplier: number,
  cnyPerUsd: number | null,
): CnyRateDecimals {
  if (cnyPerUsd === null) {
    return 2;
  }
  const cnyValues: number[] = [];
  for (const line of lines) {
    if (line.notBilled) {
      continue;
    }
    cnyValues.push(line.value * rateMultiplier * cnyPerUsd);
    if (hasRateDiscount(rateMultiplier, baseRateMultiplier)) {
      cnyValues.push(line.value * baseRateMultiplier * cnyPerUsd);
    }
  }
  return cnyRateDecimalsForCard(cnyValues);
}

/** One placed cell of a two-axis tier matrix. */
type TierMatrixCell =
  | { readonly kind: 'single_rate'; readonly perMtokUsd: number | null }
  | { readonly kind: 'unit_rate'; readonly rateUsdMicros: number };

interface TierMatrixModel {
  readonly rowAxis: CatalogBillingTiers['axes'][number];
  readonly colAxis: CatalogBillingTiers['axes'][number];
  readonly cells: ReadonlyMap<string, TierMatrixCell>;
}

function tierMatrixCellKey(rowValueKey: string, colValueKey: string): string {
  // NUL cannot appear in an axis value key, so the joint is unambiguous.
  return `${rowValueKey}\u0000${colValueKey}`;
}

/**
 * Try to arrange two-axis tier rows as a matrix (checklist gap #2). The
 * arrangement only holds when every priced row is a SCALAR price (one
 * figure per cell — a seven-bucket token card cannot sit in a grid cell)
 * that lands on exactly one (row, column) pair of LISTED axis values, at
 * most once. Anything else returns null and the caller keeps the row
 * list, which can render every row honestly — a priced row is never
 * dropped to make a grid work.
 */
function buildTierMatrix(
  rowAxis: CatalogBillingTiers['axes'][number],
  colAxis: CatalogBillingTiers['axes'][number],
  rows: CatalogBillingTiers['rows'],
): TierMatrixModel | null {
  const rowKeys = new Set(rowAxis.values.map((value) => value.key));
  const colKeys = new Set(colAxis.values.map((value) => value.key));
  const cells = new Map<string, TierMatrixCell>();
  for (const row of rows) {
    const rates = classifyTierRates(row.rates);
    if (rates.kind !== 'single_rate' && rates.kind !== 'unit_rate') {
      return null;
    }
    const rowValueKey = row.variant[rowAxis.key];
    const colValueKey = row.variant[colAxis.key];
    if (
      typeof rowValueKey !== 'string' ||
      typeof colValueKey !== 'string' ||
      !rowKeys.has(rowValueKey) ||
      !colKeys.has(colValueKey)
    ) {
      return null;
    }
    const cellKey = tierMatrixCellKey(rowValueKey, colValueKey);
    if (cells.has(cellKey)) {
      return null;
    }
    cells.set(cellKey, rates);
  }
  return { rowAxis, colAxis, cells };
}

/**
 * The display order of the row LIST: rows sort by their values' display
 * position on each axis in axis order, so a semantically sorted axis
 * (gap #3) orders the list exactly as it would order a matrix. A variant
 * value no axis lists ranks last (it still renders, labeled verbatim —
 * the #117 rule); ties keep the wire order, the sort being stable.
 */
function orderTierRows(
  axes: CatalogBillingTiers['axes'],
  rows: CatalogBillingTiers['rows'],
): CatalogBillingTiers['rows'] {
  const rankOnAxis = (row: CatalogBillingTierRow, axis: CatalogBillingTiers['axes'][number]) => {
    const valueKey = row.variant[axis.key];
    if (typeof valueKey !== 'string') {
      return Number.POSITIVE_INFINITY;
    }
    const index = axis.values.findIndex((value) => value.key === valueKey);
    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };
  return [...rows].sort((a, b) => {
    for (const axis of axes) {
      const delta = rankOnAxis(a, axis) - rankOnAxis(b, axis);
      if (delta !== 0) {
        return delta;
      }
    }
    return 0;
  });
}

/**
 * Geometries ② and ③-a/b: the tiered price surface. The price list is the
 * display source — a cell with no price row is not sellable — so:
 *
 *   - TWO axes whose rows are all scalar prices render as a real matrix
 *     (rows = first axis, columns = second axis); a listed-but-unpriced
 *     cell shows an em dash, stating "not sellable" in place;
 *   - one axis (or none) keeps the incumbent row list;
 *   - three or more axes (no SKU sells this today) ALSO fall back to the
 *     row list: a grid has two directions, and flattening a third axis
 *     into either one would fabricate a hierarchy the document does not
 *     state — the row list stays honest at any arity;
 *   - rows a matrix cannot hold exactly (a seven-bucket card per tier, an
 *     unknown rates member, an unlisted variant value) keep the row list
 *     too, so no priced row is ever dropped for layout.
 *
 * Axis values order semantically in BOTH layouts (gap #3).
 */
function TieredPrices({
  sku,
  tiers,
  unitLabel,
  cnyPerUsd,
  locale,
}: {
  readonly sku: CatalogSku;
  readonly tiers: CatalogBillingTiers;
  readonly unitLabel: BilingualLabel | null;
  readonly cnyPerUsd: number | null;
  readonly locale: Locale;
}): JSX.Element {
  const t = useT();
  if (tiers.rows.length === 0) {
    // Zero priced cells is the same "pricing pending" state as
    // `unit_pricing.rates: []` (#1706): never a free model, never ¥0.00.
    return (
      <p className="m-0 text-body text-ink-low" data-testid="pricing-unit-pending">
        {t('pricing.rate.unit.pending')}
      </p>
    );
  }
  const unit = unitLabel === null ? null : pickLabel(unitLabel, locale);
  // Display-ordered axes (gap #3): the backend emits dictionary order.
  const axes = tiers.axes.map((axis) => ({ ...axis, values: orderAxisValues(axis.values) }));
  const caption = axesCaption(axes, unit, locale, t);
  const firstAxis = axes[0];
  const secondAxis = axes[1];
  if (axes.length === 2 && firstAxis !== undefined && secondAxis !== undefined) {
    const matrix = buildTierMatrix(firstAxis, secondAxis, tiers.rows);
    if (matrix !== null) {
      return (
        <TierMatrix
          matrix={matrix}
          caption={caption}
          locale={locale}
          rateMultiplier={sku.rate_multiplier}
          baseRateMultiplier={sku.base_rate_multiplier}
          cnyPerUsd={cnyPerUsd}
          notBilledLabel={t('pricing.rate.not_billed')}
        />
      );
    }
  }
  const orderedRows = orderTierRows(axes, tiers.rows);
  const decimals = tierListDecimals(
    orderedRows,
    sku.rate_multiplier,
    sku.base_rate_multiplier,
    cnyPerUsd,
  );
  return (
    <div className="flex min-w-[200px] flex-col gap-2" data-testid="pricing-billing-tiers">
      {caption !== null ? <p className="m-0 text-caption text-ink-low">{caption}</p> : null}
      <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-3">
        {orderedRows.map((row) => (
          <TierRowLine
            key={tierRowKey(row)}
            row={row}
            label={tierRowLabel(axes, row.variant, locale)}
            unitLabel={unit}
            rateMultiplier={sku.rate_multiplier}
            baseRateMultiplier={sku.base_rate_multiplier}
            cnyPerUsd={cnyPerUsd}
            decimals={decimals}
          />
        ))}
      </dl>
    </div>
  );
}

/**
 * The caption over a tiered surface: the axis names in axis order through
 * the backend labels, plus the unit of sale ("Per second") when the
 * document names one — the matrix cells carry bare figures, so the unit
 * must be stated once at the frame level.
 */
function axesCaption(
  axes: CatalogBillingTiers['axes'],
  unit: string | null,
  locale: Locale,
  t: ReturnType<typeof useT>,
): string | null {
  const parts: string[] = [];
  if (axes.length > 0) {
    parts.push(axes.map((axis) => pickLabel(axis.label, locale)).join(' × '));
  }
  if (unit !== null && unit !== '') {
    parts.push(t('pricing.billing.per_unit', { unit }));
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * Card precision for the tier ROW LIST (checklist gaps #1/#4): scalar
 * token rows and seven-bucket card lines feed the jiao scan with their
 * effective and (under a discount) struck CNY amounts. Per-unit LIST rows
 * do not participate: they keep the fixed 6-decimal micro channel from
 * #1706 (`UnitPriceLine`), where sub-cent rates live.
 */
function tierListDecimals(
  rows: CatalogBillingTiers['rows'],
  rateMultiplier: number,
  baseRateMultiplier: number,
  cnyPerUsd: number | null,
): CnyRateDecimals {
  if (cnyPerUsd === null) {
    return 2;
  }
  const cnyValues: number[] = [];
  const push = (usdRate: number): void => {
    cnyValues.push(usdRate * rateMultiplier * cnyPerUsd);
    if (hasRateDiscount(rateMultiplier, baseRateMultiplier)) {
      cnyValues.push(usdRate * baseRateMultiplier * cnyPerUsd);
    }
  };
  for (const row of rows) {
    const rates = classifyTierRates(row.rates);
    if (rates.kind === 'single_rate' && rates.perMtokUsd !== null && rates.perMtokUsd !== 0) {
      push(rates.perMtokUsd);
    } else if (rates.kind === 'token_card') {
      for (const line of billingTokenCardLines(rates.rates)) {
        if (!line.notBilled) {
          push(line.value);
        }
      }
    }
  }
  return cnyRateDecimalsForCard(cnyValues);
}

/**
 * The two-axis matrix (checklist gap #2): rows = first axis, columns =
 * second axis, all values in display order. A cell the price list does
 * not spell renders an em dash — listed on an axis but not sellable at
 * this intersection. All figures share ONE card precision; a `0` price
 * renders "Not billed" (gap #4) and a discount strikes the base price
 * under the effective one, exactly like the line renderers.
 */
function TierMatrix({
  matrix,
  caption,
  locale,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  notBilledLabel,
}: {
  readonly matrix: TierMatrixModel;
  readonly caption: string | null;
  readonly locale: Locale;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly notBilledLabel: string;
}): JSX.Element {
  const decimals = tierMatrixDecimals(matrix, rateMultiplier, baseRateMultiplier, cnyPerUsd);
  const headerCellClass =
    'px-2 py-1.5 text-left font-mono text-mono-label font-normal text-ink-low';
  return (
    <div
      className="flex min-w-[200px] flex-col gap-2 overflow-x-auto"
      data-testid="pricing-billing-tier-matrix"
    >
      {caption !== null ? <p className="m-0 text-caption text-ink-low">{caption}</p> : null}
      <table className="w-auto border-collapse">
        <thead>
          <tr className="border-b border-line-soft">
            {/* The corner over the row headers labels nothing: a plain
                data cell, so it claims no columnheader role. */}
            <td className={headerCellClass} />
            {matrix.colAxis.values.map((colValue) => (
              <th key={colValue.key} scope="col" className={headerCellClass}>
                {labelOrKey(colValue, locale)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.rowAxis.values.map((rowValue) => (
            <tr key={rowValue.key} className="border-b border-line-soft last:border-b-0">
              <th scope="row" className={headerCellClass}>
                {labelOrKey(rowValue, locale)}
              </th>
              {matrix.colAxis.values.map((colValue) => (
                <TierMatrixCellView
                  key={colValue.key}
                  cell={matrix.cells.get(tierMatrixCellKey(rowValue.key, colValue.key))}
                  testId={`pricing-billing-matrix-cell-${rowValue.key}-${colValue.key}`}
                  decimals={decimals}
                  rateMultiplier={rateMultiplier}
                  baseRateMultiplier={baseRateMultiplier}
                  cnyPerUsd={cnyPerUsd}
                  notBilledLabel={notBilledLabel}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Card precision over every figure the matrix will show (see gap #2). */
function tierMatrixDecimals(
  matrix: TierMatrixModel,
  rateMultiplier: number,
  baseRateMultiplier: number,
  cnyPerUsd: number | null,
): CnyRateDecimals {
  if (cnyPerUsd === null) {
    return 2;
  }
  const cnyValues: number[] = [];
  const push = (cnyEffective: number, cnyBase: number): void => {
    cnyValues.push(cnyEffective);
    if (hasRateDiscount(rateMultiplier, baseRateMultiplier)) {
      cnyValues.push(cnyBase);
    }
  };
  for (const cell of matrix.cells.values()) {
    if (cell.kind === 'unit_rate') {
      push(
        (cell.rateUsdMicros * rateMultiplier * cnyPerUsd) / 1_000_000,
        (cell.rateUsdMicros * baseRateMultiplier * cnyPerUsd) / 1_000_000,
      );
    } else if (cell.perMtokUsd !== null && cell.perMtokUsd !== 0) {
      push(
        cell.perMtokUsd * rateMultiplier * cnyPerUsd,
        cell.perMtokUsd * baseRateMultiplier * cnyPerUsd,
      );
    }
  }
  return cnyRateDecimalsForCard(cnyValues);
}

function TierMatrixCellView({
  cell,
  testId,
  decimals,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  notBilledLabel,
}: {
  readonly cell: TierMatrixCell | undefined;
  readonly testId: string;
  readonly decimals: CnyRateDecimals;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly notBilledLabel: string;
}): JSX.Element {
  const cellClass = 'px-2 py-1.5 align-top font-mono text-body font-medium tabular-nums';
  const emDash = (
    <td className={`${cellClass} text-ink-low`} data-testid={testId}>
      —
    </td>
  );
  if (cell === undefined) {
    // Listed on both axes, priced on neither row: not sellable here.
    return emDash;
  }
  let effective: string;
  let struckBase: string;
  if (cell.kind === 'unit_rate') {
    effective = trimCnyTrailingZeros(
      formatUsdMicrosAsCnyRate(cell.rateUsdMicros * rateMultiplier, cnyPerUsd, decimals),
    );
    struckBase = trimCnyTrailingZeros(
      formatUsdMicrosAsCnyRate(cell.rateUsdMicros * baseRateMultiplier, cnyPerUsd, decimals),
    );
  } else {
    const perMtokUsd = cell.perMtokUsd;
    if (perMtokUsd === null) {
      return emDash;
    }
    if (perMtokUsd === 0) {
      // `0` is a price — deliberately free — and must never read as the
      // absent em dash (gap #4).
      return (
        <td className={`${cellClass} text-ink`} data-testid={testId}>
          {notBilledLabel}
        </td>
      );
    }
    effective = trimCnyTrailingZeros(
      formatCnyRateFromUsdRate(perMtokUsd * rateMultiplier, cnyPerUsd, decimals),
    );
    struckBase = trimCnyTrailingZeros(
      formatCnyRateFromUsdRate(perMtokUsd * baseRateMultiplier, cnyPerUsd, decimals),
    );
  }
  const struck = hasRateDiscount(rateMultiplier, baseRateMultiplier) ? struckBase : null;
  return (
    <td className={`${cellClass} text-ink`} data-testid={testId}>
      <span className="flex flex-col gap-0.5">
        <span>{effective}</span>
        {struck !== null ? (
          <span className="font-mono text-mono-label font-normal tabular-nums text-ink-low line-through">
            {struck}
          </span>
        ) : null}
      </span>
    </td>
  );
}

function labelOrKey(
  value: { readonly key: string; readonly label: BilingualLabel },
  locale: Locale,
): string {
  const label = pickLabel(value.label, locale);
  return label === '' ? value.key : label;
}

function TierRowLine({
  row,
  label,
  unitLabel,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  decimals,
}: {
  readonly row: CatalogBillingTierRow;
  readonly label: string;
  readonly unitLabel: string | null;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly decimals: CnyRateDecimals;
}): JSX.Element {
  const t = useT();
  const rates = classifyTierRates(row.rates);
  switch (rates.kind) {
    case 'unit_rate':
      if (unitLabel === null || unitLabel === '') {
        // A per-unit price whose unit of sale the document does not name
        // cannot be rendered truthfully — same rule as an unknown member.
        return <UnknownTierRates label={label} />;
      }
      return (
        <UnitPriceLine
          label={label}
          rateUsdMicros={rates.rateUsdMicros}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
          unitSuffix={unitLabel}
        />
      );
    case 'single_rate':
      // One $/Mtok per tier: input and output bill at the same rate and
      // no cache counter exists upstream (`CatalogBillingSingleRate`).
      // `0` renders "Not billed" — a real, deliberately free price —
      // while `null` renders the em dash (gap #4).
      return (
        <PriceLine
          label={label}
          value={rates.perMtokUsd}
          notBilled={rates.perMtokUsd === 0}
          notBilledLabel={t('pricing.rate.not_billed')}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
          decimals={decimals}
        />
      );
    case 'token_card':
      // A full seven-bucket card per tier: the tier name heads its own
      // bucket grid, driven by the document's null/0 rule (gap #4) — the
      // per-vendor masks stay with the legacy flat fields.
      return (
        <div className="col-span-2 min-w-0">
          <dt className="mb-1 truncate text-mono-label text-ink-low">{label}</dt>
          <dd className="m-0">
            <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-3">
              {billingTokenCardLines(rates.rates).map((line) => (
                <PriceLine
                  key={line.labelKey}
                  label={t(line.labelKey)}
                  value={line.value}
                  notBilled={line.notBilled}
                  notBilledLabel={t('pricing.rate.not_billed')}
                  rateMultiplier={rateMultiplier}
                  baseRateMultiplier={baseRateMultiplier}
                  cnyPerUsd={cnyPerUsd}
                  decimals={decimals}
                />
              ))}
            </dl>
          </dd>
        </div>
      );
    case 'unknown':
      return <UnknownTierRates label={label} />;
  }
}

/**
 * A rates member this reader does not recognize (or cannot render
 * truthfully): the tier stays listed so the cell count of the price list
 * is honest, but no price is shown — the same no-price-over-wrong-price
 * trade the presentation_version fallback makes.
 */
function UnknownTierRates({ label }: { readonly label: string }): JSX.Element {
  return (
    <div className="min-w-0" data-testid="pricing-billing-tier-unknown">
      <dt className="mb-1 truncate text-mono-label text-ink-low">{label}</dt>
      <dd className="m-0 font-mono text-body font-medium tabular-nums text-ink-low">—</dd>
    </div>
  );
}

type TranslateFn = ReturnType<typeof useT>;

/**
 * tenant#138: `prompt_length_tiers` is omitted when the SKU has none,
 * never `[]`. A present-but-malformed ladder is treated as absent so
 * the base `token_rates` card still renders — better a short-prompt
 * price than a guessed long-prompt one.
 */
function readPromptLengthTiers(
  value: CatalogSkuBilling['prompt_length_tiers'],
): CatalogBillingPromptLengthTiers | null {
  if (value == null || !isRecordObject(value) || !Array.isArray(value.tiers)) {
    return null;
  }
  if (value.tiers.length === 0) {
    return null;
  }
  if (
    !value.tiers.every(
      (tier) =>
        isRecordObject(tier) &&
        typeof tier.min_prompt_tokens === 'number' &&
        Number.isFinite(tier.min_prompt_tokens) &&
        tier.min_prompt_tokens > 0 &&
        isRecordObject(tier.rates),
    )
  ) {
    return null;
  }
  return value;
}

/**
 * tenant#138: surcharges are legal on BOTH lanes. Empty / absent / a
 * malformed line is dropped rather than quoted.
 */
function readSurcharges(
  billing: CatalogSkuBilling | undefined,
): readonly CatalogBillingSurcharge[] {
  if (billing == null || !isRecordObject(billing) || !Array.isArray(billing.surcharges)) {
    return [];
  }
  return billing.surcharges.filter(
    (item) =>
      isRecordObject(item) &&
      typeof item.unit === 'string' &&
      typeof item.variant_key === 'string' &&
      typeof item.rate_usd_micros_per_unit === 'number' &&
      Number.isFinite(item.rate_usd_micros_per_unit) &&
      isRecordObject(item.label),
  );
}

function readSaleSchedule(schedule: CatalogSku['sale_schedule']): CatalogSkuSaleSchedule | null {
  if (schedule == null || !isRecordObject(schedule)) {
    return null;
  }
  if (typeof schedule.timezone !== 'string' || schedule.timezone.length === 0) {
    return null;
  }
  if (!Array.isArray(schedule.windows) || schedule.windows.length === 0) {
    return null;
  }
  if (
    !schedule.windows.every(
      (window) =>
        isRecordObject(window) &&
        typeof window.days_of_week === 'number' &&
        Number.isFinite(window.days_of_week) &&
        typeof window.start_minute === 'number' &&
        Number.isFinite(window.start_minute) &&
        typeof window.end_minute === 'number' &&
        Number.isFinite(window.end_minute) &&
        typeof window.sale_multiplier === 'number' &&
        Number.isFinite(window.sale_multiplier),
    )
  ) {
    return null;
  }
  return schedule;
}

/**
 * Body + the two additive blocks that sit on every lane (tenant#138).
 * `includeSurcharges` is false when the body already came from the
 * #1706 `unit_pricing` projection, which lists every (unit, variant)
 * row — appending `billing.surcharges` would quote the same image row
 * twice. Sale windows always append: they are a SKU-level schedule,
 * not a second price list.
 */
function withBillingExtras(
  body: JSX.Element,
  sku: CatalogSku,
  locale: Locale,
  cnyPerUsd: number | null,
  includeSurcharges: boolean,
): JSX.Element {
  const surcharges = includeSurcharges ? readSurcharges(sku.billing) : [];
  const sale = readSaleSchedule(sku.sale_schedule);
  if (surcharges.length === 0 && sale === null) {
    return body;
  }
  return (
    <div className="flex min-w-[200px] flex-col gap-3">
      {body}
      {surcharges.length > 0 ? (
        <SurchargesBlock
          surcharges={surcharges}
          locale={locale}
          rateMultiplier={sku.rate_multiplier}
          baseRateMultiplier={sku.base_rate_multiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ) : null}
      {sale !== null ? <SaleWindowsBlock schedule={sale} locale={locale} /> : null}
    </div>
  );
}

const SALE_DAY_KEYS: readonly I18nKey[] = [
  'pricing.billing.sale.day.mon',
  'pricing.billing.sale.day.tue',
  'pricing.billing.sale.day.wed',
  'pricing.billing.sale.day.thu',
  'pricing.billing.sale.day.fri',
  'pricing.billing.sale.day.sat',
  'pricing.billing.sale.day.sun',
];

function saleDayLabel(t: TranslateFn, bit: number): string {
  const key = SALE_DAY_KEYS[bit];
  return key === undefined ? '' : t(key);
}

function formatSaleDayRange(t: TranslateFn, locale: Locale, mask: number): string {
  const spans = weekdaySpans(weekdayIndexesFromMask(mask));
  if (spans.length === 0) {
    return '';
  }
  const joiner = locale === 'zh-CN' ? '、' : ', ';
  return spans
    .map((span) => {
      const start = saleDayLabel(t, span.start);
      if (span.start === span.end) {
        return start;
      }
      return `${start}–${saleDayLabel(t, span.end)}`;
    })
    .filter((part) => part.length > 0)
    .join(joiner);
}

function promptTierBasisHint(t: TranslateFn, basis: CatalogBillingPromptTierBasis): string | null {
  switch (basis) {
    case 'varies_by_ingress':
      return t('pricing.billing.prompt_tier.basis.varies_by_ingress');
    case 'input_tokens':
      return t('pricing.billing.prompt_tier.basis.input_tokens');
    case 'input_plus_cache_tokens':
      return t('pricing.billing.prompt_tier.basis.input_plus_cache_tokens');
    default:
      // Unknown basis: show the ladder, do not invent a formula (tenant#138).
      return null;
  }
}

/**
 * tenant#138 form ⑥ — dispatch a rates member by field presence. A
 * single-rate card is ONE number (never restated as seven buckets);
 * unrecognized shapes render nothing rather than a wrong price.
 */
function RatesMember({
  rates,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  tokenCardTestId,
  label,
  unitLabel,
  decimals,
}: {
  readonly rates: CatalogBillingTierRow['rates'];
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly tokenCardTestId?: string;
  readonly label?: string;
  readonly unitLabel?: string | null;
  readonly decimals?: CnyRateDecimals;
}): JSX.Element | null {
  const t = useT();
  const view = classifyTierRates(rates);
  switch (view.kind) {
    case 'unknown':
      return null;
    case 'single_rate':
      return (
        <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
          <PriceLine
            label={label ?? t('pricing.billing.prompt_tier.base')}
            value={view.perMtokUsd}
            notBilled={view.perMtokUsd === 0}
            notBilledLabel={t('pricing.rate.not_billed')}
            rateMultiplier={rateMultiplier}
            baseRateMultiplier={baseRateMultiplier}
            cnyPerUsd={cnyPerUsd}
            decimals={decimals ?? 2}
          />
        </dl>
      );
    case 'unit_rate': {
      if (unitLabel === null || unitLabel === undefined || unitLabel === '') {
        return null;
      }
      return (
        <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
          <UnitPriceLine
            label={label ?? t('pricing.billing.per_unit', { unit: unitLabel })}
            rateUsdMicros={view.rateUsdMicros}
            rateMultiplier={rateMultiplier}
            baseRateMultiplier={baseRateMultiplier}
            cnyPerUsd={cnyPerUsd}
            unitSuffix={label === undefined ? undefined : unitLabel}
          />
        </dl>
      );
    }
    case 'token_card':
      return (
        <BillingTokenCard
          rates={view.rates}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
          testId={tokenCardTestId}
        />
      );
  }
}

/**
 * tenant#138: prompt-length ladder. The full price list is N+1 rows —
 * base `token_rates` plus one row per tier — and each tier REPLACES the
 * whole card, it does not overlay a cell. `basis` is a hint, never a
 * client-side threshold the page evaluates.
 */
function PromptLengthPriceList({
  baseRates,
  promptLengthTiers,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly baseRates: CatalogBillingTokenRates;
  readonly promptLengthTiers: CatalogBillingPromptLengthTiers;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const hint = promptTierBasisHint(t, promptLengthTiers.basis);
  return (
    <div className="flex min-w-[200px] flex-col gap-3" data-testid="pricing-billing-prompt-tiers">
      {hint !== null ? (
        <p className="m-0 text-caption text-ink-low" data-testid="pricing-billing-prompt-basis">
          {hint}
        </p>
      ) : null}
      <PromptLengthTierRow
        testId="pricing-billing-prompt-tier-base"
        label={t('pricing.billing.prompt_tier.base')}
        rates={baseRates}
        rateMultiplier={rateMultiplier}
        baseRateMultiplier={baseRateMultiplier}
        cnyPerUsd={cnyPerUsd}
      />
      {promptLengthTiers.tiers.map((tier) => (
        <PromptLengthTierRow
          key={tier.min_prompt_tokens}
          testId={`pricing-billing-prompt-tier-${tier.min_prompt_tokens}`}
          label={t('pricing.billing.prompt_tier.long', { n: tier.min_prompt_tokens })}
          rates={tier.rates}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </div>
  );
}

function PromptLengthTierRow({
  testId,
  label,
  rates,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly testId: string;
  readonly label: string;
  readonly rates: CatalogBillingTokenRates;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId}>
      <p className="m-0 truncate text-mono-label text-ink-low">{label}</p>
      <RatesMember
        rates={rates}
        rateMultiplier={rateMultiplier}
        baseRateMultiplier={baseRateMultiplier}
        cnyPerUsd={cnyPerUsd}
      />
    </div>
  );
}

function surchargeLineLabel(
  surcharge: CatalogBillingSurcharge,
  locale: Locale,
  t: TranslateFn,
): string | null {
  const unit = pickLabel(surcharge.label, locale);
  if (unit === '') {
    return null;
  }
  const base = t('pricing.billing.per_unit', { unit });
  return surcharge.variant_key === '' ? base : `${base} · ${surcharge.variant_key}`;
}

function SurchargesBlock({
  surcharges,
  locale,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly surcharges: readonly CatalogBillingSurcharge[];
  readonly locale: Locale;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element | null {
  const t = useT();
  const lines = surcharges.flatMap((surcharge) => {
    const label = surchargeLineLabel(surcharge, locale, t);
    if (label === null) {
      return [];
    }
    return [{ surcharge, label }];
  });
  if (lines.length === 0) {
    return null;
  }
  return (
    <div className="flex min-w-[200px] flex-col gap-2" data-testid="pricing-billing-surcharges">
      <p className="m-0 text-caption text-ink-low">{t('pricing.billing.surcharges')}</p>
      <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-3">
        {lines.map(({ surcharge, label }) => (
          <UnitPriceLine
            key={`${surcharge.unit}:${surcharge.variant_key}`}
            label={label}
            rateUsdMicros={surcharge.rate_usd_micros_per_unit}
            rateMultiplier={rateMultiplier}
            baseRateMultiplier={baseRateMultiplier}
            cnyPerUsd={cnyPerUsd}
          />
        ))}
      </dl>
    </div>
  );
}

function SaleWindowsBlock({
  schedule,
  locale,
}: {
  readonly schedule: CatalogSkuSaleSchedule;
  readonly locale: Locale;
}): JSX.Element | null {
  const t = useT();
  const rows = schedule.windows.flatMap((window, index) => {
    const days = formatSaleDayRange(t, locale, window.days_of_week);
    const start = formatMinuteOfDay(window.start_minute);
    const end = formatMinuteOfDay(window.end_minute);
    if (days === '' || start === '' || end === '') {
      return [];
    }
    return [
      {
        key: `${window.days_of_week}-${window.start_minute}-${window.end_minute}-${index}`,
        days,
        time: `${start}–${end}`,
        multiplier: window.sale_multiplier,
      },
    ];
  });
  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="flex min-w-[200px] flex-col gap-2" data-testid="pricing-billing-sale-windows">
      <p className="m-0 text-caption text-ink-low">
        {t('pricing.billing.sale_windows', { timezone: schedule.timezone })}
      </p>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {rows.map((row) => (
          <li
            key={row.key}
            className="m-0 font-mono text-body tabular-nums text-ink"
            data-testid={`pricing-billing-sale-window-${row.key}`}
          >
            <span>{row.days}</span>
            <span className="text-ink-low">{' · '}</span>
            <span>{row.time}</span>
            <span className="text-ink-low">{' · '}</span>
            <span>{`×${row.multiplier}`}</span>
          </li>
        ))}
      </ul>
      <p className="m-0 text-caption text-ink-low" data-testid="pricing-billing-sale-stacking">
        {t('pricing.billing.sale_windows.stacking')}
      </p>
    </div>
  );
}
